import styled from 'styled-components'
import {createPortal} from 'react-dom'
import React from 'react'
import RenderIf from "src/components/views/mkt04/RenderIf";
import { AnimatePresence, motion, type Variants, type Transition } from 'framer-motion';
import {ModalProps} from "src/components/views/mkt04/Modal/index";
import {ModalSpace} from "src/components/views/mkt04/Modal/provider";
import {Button} from "src/components/views/mkt04/Button";
import {IconButton} from "src/components/views/mkt04/Button/IconButton";

const springTransition: Transition = { type: 'spring', stiffness: 500, damping: 40 };

const toCssSize = (v?: number | string) =>
    v == null ? undefined : typeof v === 'number' ? `${v}px` : v;


const ModalView = ({
                       modalData,
                       onSubmitInternal,
                       onCancelInternal,
                       placement = 'center',
                       offset = 16,
                   }: ModalProps) => {
    const {actions, contents,width, maxWidth} = modalData as ModalSpace.ModalData
    const modalVars: Variants = {
        start:  { opacity: 0, y: placement === 'bottom' ? 20 : 100 },
        end:    { opacity: 1, y: 0, transition: springTransition },
        finish: { opacity: 0, y: placement === 'bottom' ? 20 : -100, transition: springTransition },
    };
    const twoButtons = !!actions?.cancel && !!actions?.submit;

    return createPortal(
        <React.Fragment>
            <AnimatePresence>
                <ModalBackGroundWrap/>
            </AnimatePresence>
          <ModalWrap $placement={placement} $offset={offset}>
                <AnimatePresence>
                    <ModalInner variants={modalVars}
                                initial="start"
                                animate="end"
                                exit="finish"
                                $placement={placement}
                                $width={toCssSize(width)}
                                $maxWidth={toCssSize(maxWidth)}  >
                        <ModalTitle $active={!!contents?.title}> {contents?.title}</ModalTitle>
                        <ModalContents $active={!!contents?.desc}>{contents?.desc}</ModalContents>
                        {contents?.children}

                        <RenderIf when={!!actions}>
                            <ModalButtonWrap>
                                {twoButtons ? (
                                    <>
                                        <ModalCancelButton onClick={onCancelInternal}>
                                            {actions!.cancel!.text}
                                        </ModalCancelButton>
                                        <ModalSubmitButton
                                            onClick={onSubmitInternal}
                                            isLoading={(actions as any)?.submit?.isLoading}
                                            disabled={(actions as any)?.submit?.disabled}
                                            icon={(actions as any)?.submit?.icon}
                                            iconSrc={(actions as any)?.submit?.iconSrc}
                                            iconSize={(actions as any)?.submit?.iconSize ?? 24}
                                            iconPosition="right"
                                            iconInline={(actions as any)?.submit?.iconInline}
                                        >
                                            {actions!.submit!.text}
                                        </ModalSubmitButton>
                                    </>
                                ) : (
                                    <>
                                        <RenderIf when={!!actions?.cancel}>
                                            <Button.Default onClick={onCancelInternal}>
                                                {actions?.cancel?.text}
                                            </Button.Default>
                                        </RenderIf>
                                        <RenderIf when={!!actions?.submit}>
                                            <Button.Primary onClick={onSubmitInternal}>
                                                {actions?.submit?.text}
                                            </Button.Primary>
                                        </RenderIf>
                                    </>
                                )}
                            </ModalButtonWrap>
                        </RenderIf>
                    </ModalInner>
                </AnimatePresence>
            </ModalWrap>
        </React.Fragment>,
        document.body,
    )
}
export default ModalView

const ModalBackGroundWrap = styled(motion.div)`
    width: 100%;
    height: 100svh;
    @supports (height: 100dvh) { height: 100dvh; }

    position: fixed;
    inset: 0;
    z-index: 5;
    overflow: hidden;

    background-color: rgba(0,0,0,0.38); 
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
`;
const ModalWrap = styled.div<{
    $placement: 'center' | 'bottom'
    $offset: number
}>`
    width: 100%;
    height: 100svh;
    @supports (height: 100dvh) { height: 100dvh; }
    position: fixed;
    z-index: 10;
    inset: 0;
    margin: auto;
    display: flex;
    flex-flow: column;
    justify-content: ${({$placement}) => ($placement === 'bottom' ? 'flex-end' : 'center')};
    align-items: center;
    padding-bottom: ${({$placement, $offset}) =>
             $placement === 'bottom'
               ? `calc(${$offset}px + max(env(safe-area-inset-bottom, 0px), 0px))`
                       : '0'};
`
const ModalInner = styled(motion.div)<{
    $placement?: 'center' | 'bottom';
    $width?: string;
    $maxWidth?: string;
}>`
    width: ${({ $width }) => $width ?? '100%'};
    max-width: ${({ $maxWidth }) => $maxWidth ?? $maxWidth};
    border-radius: 20px;
    padding: 20px;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 12px;
`;

const ModalTitle = styled.strong<{
    $active: boolean
}>`
    display: ${(props) => (props.$active ? ' block' : 'none')};
    font-family: 'Pretendard', sans-serif;
    font-size: 16px;
    text-align: center;
    line-height: 1.5;
    word-break: keep-all;
    white-space: pre-line;
`
const ModalContents = styled.p<{
    $active: boolean
}>`
    display: ${(props) => (props.$active ? 'block' : 'none')};
    font-family: 'Pretendard', sans-serif;
    font-size: 14px;
    word-break: keep-all;
    white-space: break-spaces;
`

const ModalButtonWrap = styled.div`
    display: flex;
    justify-content: center;
    gap: 6px;
    margin-top: 8px;
`

export const ModalInnerScroll = styled.div`
    width: 100%;
    max-height: 116px;
    overflow: hidden;
    overflow-y: auto;
    background-color: #FAFAFA;
    padding: 10px;
    border-radius: 16px;
    line-height: 1.4;
    text-align: left;
    white-space: break-spaces;
`

export const ModalInnerText = styled.p`
    word-break: keep-all;
    line-height: 1.4;
`
const ModalCancelButton = styled(Button.Default)`
  width: 61px;
  flex: 0 0 61px;
`;

const ModalSubmitButton = styled(IconButton)`
  min-width: 193px;
  flex: 1;
`;